import * as Comparators from "portal/utils/comparators";
import { areArraysEqual } from "portal/utils/equality";
import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { classes, ThemeProps } from "portal/theme/theme";
import { getClass, RobotClass, robotSort } from "portal/utils/robots";
import { portalApi } from "portal/state/portalApi";
import { ReportInstanceResponse } from "protos/portal/reports";
import { RobotResponse, RobotSummaryResponse } from "protos/portal/robots";
import { TFunction } from "i18next";
import { titleCase, valueOrLoading } from "portal/utils/strings";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useStableArray } from "portal/utils/hooks/useStable";
import { useTranslation } from "react-i18next";
import { WithSkeleton } from "portal/components/WithSkeleton";
import React, { FunctionComponent, SyntheticEvent, useEffect } from "react";

interface Props {
  className?: string;
  customerIds?: number[];
  disabled?: boolean;
  maxRobots?: number;
  onChange?: (selectedRobots: number[]) => void;
  onOpen?: (event: SyntheticEvent) => void;
  onClose?: (event: SyntheticEvent) => void;
  readOnly?: boolean;
  selectedRobots?: number[];
  themeProps?: Partial<ThemeProps>;
  instance?: ReportInstanceResponse;
  label?: string;
  onlyShowRobotClass?: RobotClass;
}

export const getSelectedRobotsDescription = (
  t: TFunction,
  selectedRobots: number[] = [],
  robots: RobotSummaryResponse[] = []
): string => {
  if (selectedRobots.length <= 2) {
    return selectedRobots
      .map((id) => {
        const summary = robots.find((summary) => summary.robot?.db?.id === id);
        return valueOrLoading(summary?.robot?.customerSerial, t);
      })
      .join(", ");
  }
  return `${selectedRobots.length} ${t("models.robots.robot", {
    count: selectedRobots.length,
  })}`;
};

export const RobotMultiSelector: FunctionComponent<Props> = ({
  className,
  customerIds,
  disabled = false,
  maxRobots,
  onChange,
  onOpen,
  onClose,
  readOnly = false,
  selectedRobots: inputSelectedRobots = [],
  instance,
  label,
  onlyShowRobotClass,
}) => {
  const { t } = useTranslation();
  // get all robots
  const { data: robots, isSuccess } = useQueryPopups(
    portalApi.endpoints.listRobots.useQuery({
      instance: instance?.slug,
    })
  );
  // keep track of selected robots
  const selectedRobots = useStableArray<number>(inputSelectedRobots);
  const filteredRobots = robots?.filter(
    (summary) =>
      summary.robot &&
      (!customerIds ||
        customerIds.length === 0 ||
        (summary.customer?.db &&
          customerIds.includes(summary.customer.db.id))) &&
      (!onlyShowRobotClass || getClass(summary.robot) === onlyShowRobotClass)
  );

  const isAtMax =
    maxRobots !== undefined &&
    selectedRobots.length >= maxRobots &&
    selectedRobots.length > 0;

  // if the customerId changes, filter the selected robots
  useEffect(() => {
    if (!customerIds || !robots) {
      return;
    }
    const filteredByCustomer = selectedRobots.filter((id) =>
      filteredRobots?.some((summary) => summary.robot?.db?.id === id)
    );
    if (areArraysEqual(filteredByCustomer, selectedRobots)) {
      return;
    }
    onChange?.(filteredByCustomer);
  }, [customerIds, filteredRobots, onChange, robots, selectedRobots]);

  let selectLabel: string;
  if (label) {
    selectLabel = label;
  } else if (selectedRobots.length > 0) {
    selectLabel = titleCase(t("models.robots.robot_other"));
  } else if (readOnly) {
    selectLabel = t("views.reports.tools.robotsLabel.none");
  } else {
    selectLabel = t("views.reports.tools.robotsLabel.select");
  }

  return (
    <WithSkeleton
      variant="rectangular"
      className={classes(className)}
      success={isSuccess}
    >
      <FormControl size="small" error={isAtMax}>
        <InputLabel>{selectLabel}</InputLabel>
        <Select<number[]>
          size="small"
          multiple
          disabled={disabled}
          readOnly={readOnly}
          value={selectedRobots}
          input={<OutlinedInput label={selectLabel} />}
          onOpen={onOpen}
          onClose={onClose}
          onChange={(event) => {
            let selectedRobots = event.target.value;
            if (typeof selectedRobots === "string") {
              selectedRobots = [Number(selectedRobots)];
            }
            const idToRobot: Map<number, RobotResponse> = new Map();
            for (const { robot } of filteredRobots || []) {
              if (robot?.db?.id !== undefined) {
                idToRobot.set(robot.db.id, robot);
              }
            }
            selectedRobots.sort(
              Comparators.comparing(
                (id) => idToRobot.get(id),
                Comparators.nullsLast(robotSort)
              )
            );
            // filter out any robots not assigned to this customer
            if (customerIds) {
              selectedRobots = selectedRobots.filter((id) =>
                filteredRobots?.some((summary) => summary.robot?.db?.id === id)
              );
            }
            if (maxRobots !== undefined && selectedRobots.length > maxRobots) {
              selectedRobots = selectedRobots.slice(0, maxRobots);
            }
            onChange?.(selectedRobots);
          }}
          defaultValue={[]}
          renderValue={(selected) => {
            if (selected.length === 0) {
              return t("views.reports.tools.robotsLabel.select");
            }
            if (selected.length === filteredRobots?.length) {
              return t("views.reports.tools.robotsLabel.all");
            }
            return getSelectedRobotsDescription(t, selected, robots);
          }}
        >
          {filteredRobots?.map(({ robot }) => {
            const robotId = robot?.db?.id ?? Number.NaN;
            const isAtMax =
              maxRobots !== undefined &&
              selectedRobots.length >= maxRobots &&
              !selectedRobots.includes(robotId);
            return (
              <MenuItem
                key={robot?.db?.id}
                value={robot?.db?.id ?? Number.NaN}
                disabled={isAtMax}
              >
                <Checkbox checked={selectedRobots.includes(robotId)} />
                <ListItemText
                  primary={valueOrLoading(robot?.customerSerial, t)}
                />
              </MenuItem>
            );
          })}
        </Select>
        {isAtMax && (
          <FormHelperText>
            {t("views.reports.tools.robotsLabel.maxReached", {
              max: maxRobots,
            })}
          </FormHelperText>
        )}
      </FormControl>
    </WithSkeleton>
  );
};
